import React, { Fragment, type ReactNode, Suspense } from 'react';

import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ReliabilityStart, ReliabilitySucceed } from '../reliability-tracker/components';
import { studioReliabilityTracker } from '../reliability-tracker/studio-reliability-tracker';
import { SentryErrorBoundary, type SentryErrorBoundaryProps } from '../sentry';

type PageSegmentProps = {
	children: ReactNode;
	fallbackComponent?: SentryErrorBoundaryProps['fallbackComponent'];
	/**
	 * If provided, wraps children in `<Suspense fallback={suspenseFallback}>`.
	 * If not provided, no Suspense added.
	 */
	suspenseFallback?: ReactNode;
	name: string;
	/**
	 * Optional predicate that decides whether an error caught by the segment
	 * should be reported to UFO via `useUFOReportError`.
	 *
	 * Defaults to `() => true` (always report). Set this to skip reporting for
	 * errors that should not impact UFO reliability metrics for this segment,
	 * e.g. expected 403s on permission-gated queries.
	 *
	 * Sentry logging via `SentryErrorBoundary` is always invoked regardless of
	 * this predicate — only the UFO interaction error is gated.
	 */
	shouldReportError?: (error: Error) => boolean;
	/**
	 * Reliability tracker `task` identifier. Defaults to `name`.
	 */
	task?: string;
	taskId?: string;
	teamName?: SentryErrorBoundaryProps['teamName'];
	touchpointId?: SentryErrorBoundaryProps['touchpointId'];
};

const DefaultFallbackComponent = () => null;

const defaultShouldReportError = () => true;

const PageSegmentInternal = ({
	name: segmentName,
	children,
	fallbackComponent = DefaultFallbackComponent,
	suspenseFallback,
	shouldReportError = defaultShouldReportError,
	task,
	taskId,
	teamName,
	touchpointId,
}: PageSegmentProps) => {
	di(useUFOReportError);
	const reportError = useUFOReportError();
	const errorName = `PageSegmentError:${segmentName}`;
	const reliabilityTask = task ?? segmentName;
	const MaybeSuspense = suspenseFallback !== undefined ? Suspense : Fragment;
	const maybeSuspenseProps = suspenseFallback !== undefined ? { fallback: suspenseFallback } : {};

	return (
		<SentryErrorBoundary
			loggerName={errorName}
			fallbackComponent={fallbackComponent}
			teamName={teamName}
			touchpointId={touchpointId}
			onError={(error) => {
				if (fg('studio_reliability_tracker_page_segment')) {
					if (shouldReportError(error)) {
						studioReliabilityTracker.fail({
							task: reliabilityTask,
							taskId,
							error,
						});
					} else {
						studioReliabilityTracker.abort({
							task: reliabilityTask,
							taskId,
							reason: error.message,
						});
					}
				}

				if (!shouldReportError(error)) {
					return;
				}

				// TODO(RAGE-3798): remove UFO interaction-error reporting once TOME dashboards migrate.
				// using `useUFOReportError` instead of `setInteractionError`
				// because `setInteractionError` will not work due to mismatching activeInteraction
				// https://atlassian.slack.com/archives/C095EAEKV63/p1756710099202429?thread_ts=1756193218.083929&cid=C095EAEKV63
				reportError({
					name: errorName,
					errorMessage: error.message,
					errorStack: error.stack,
				});
			}}
		>
			{fg('studio_reliability_tracker_page_segment') && (
				<ReliabilityStart task={reliabilityTask} taskId={taskId} touchpointId={touchpointId} />
			)}
			<MaybeSuspense {...maybeSuspenseProps}>
				{children}
				{fg('studio_reliability_tracker_page_segment') && (
					// Order is important, this should be fired last after suspense
					<ReliabilitySucceed task={reliabilityTask} taskId={taskId} />
				)}
			</MaybeSuspense>
		</SentryErrorBoundary>
	);
};

/**
 * @param name - The name of the segment. Used for error handling and UFOSegment name.
 * @param children - The children of the segment.
 * @param fallbackComponent - The component to render when an error occurs.
 * @returns A component that wraps a segment of the page and provides error handling.
 */
export const PageSegment = (props: PageSegmentProps): React.JSX.Element => (
	// TODO(RAGE-3798): remove <UFOSegment> wrapper once TOME dashboards migrate.
	<UFOSegment name={props.name}>
		<PageSegmentInternal {...props} />
	</UFOSegment>
);
